import React from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { type SearchResultMessagePartial, type ThirdPartyUser } from '@atlassian/search-client';

import { serveFederatedSlackExperiment } from '../../../../../../../../common/utils/experiments/experiment-utils';

import { i18n } from './messages';

const USER_DISPLAY_LIMIT = 3;
interface ThreadTitleProps {
	partial: SearchResultMessagePartial;
}

const dedupUsers = (
	userMap: Record<string, { thirdPartyUser: ThirdPartyUser; index: number }>,
	user: ThirdPartyUser,
	index: number,
) => {
	if (!userMap[user.id]) {
		userMap[user.id] = { thirdPartyUser: user, index };
	}
	return userMap;
};

const getTitleMessageValues = (intl: IntlShape, users: (ThirdPartyUser | undefined)[] = []) => {
	const { formatMessage } = intl;
	const uniqueUsers = users
		?.filter((thirdPartyUser): thirdPartyUser is ThirdPartyUser => Boolean(thirdPartyUser?.name))
		?.reduce(dedupUsers, {});
	const validContributors = Object.values(uniqueUsers)
		.sort((a, b) => a.index - b.index)
		.map((entry) => entry?.thirdPartyUser?.name || formatMessage(i18n.externalPersonLabel))
		.map((name) => name?.split(' ')[0]);

	const externalPersonLabel = formatMessage(i18n.externalPersonLabel);
	const externalPersonCount = validContributors.filter(
		(name) => name === externalPersonLabel,
	).length;

	let adjustedContributors = validContributors;
	if (externalPersonCount > 1) {
		adjustedContributors = validContributors.filter((name) => name !== externalPersonLabel);
		adjustedContributors.unshift(formatMessage(i18n.externalPeopleLabel));
	}

	const contributorCount = adjustedContributors.length;
	const namedContributors = adjustedContributors.slice(0, USER_DISPLAY_LIMIT);
	const overflowContributorCount = adjustedContributors.slice(USER_DISPLAY_LIMIT).length;

	return { contributorCount, namedContributors, overflowContributorCount };
};

const getRegularThreadTitleValues = (partial: any, intl: IntlShape, formatMessage: any) => {
	const linkedEntities = partial.linkedEntities || [];
	const threadUsers = [partial, ...linkedEntities].map(
		(linkedMessage) =>
			linkedMessage?.entity?.createdBy?.thirdPartyUser || linkedMessage?.initialContributors?.[0],
	);

	const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
		intl,
		threadUsers,
	);

	const [name1, name2, name3] = namedContributors;
	const channelName =
		partial?.entity?.container?.displayName ||
		partial?.containerName ||
		formatMessage(i18n.unknownChannel);

	return {
		values: {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
			channelName: channelName,
		},
		titleMessage: i18n.threadTitleWithOthers,
	};
};

export const ThreadTitle = ({ partial }: ThreadTitleProps): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;
	let values = {};
	let titleMessage = i18n.groupChatTitle;

	const isSlackFederatedSearchEnabled =
		serveFederatedSlackExperiment(undefined, undefined, { fireExperimentExposure: false }) ||
		fg('ai_3p_slack_federated_search_enabled');

	const isSlackIntegration = partial.integrationId === 'ari:cloud:platform::integration/slack';
	if (partial.containerName != null && isSlackFederatedSearchEnabled && isSlackIntegration) {
		const groupChatParticipants = partial?.initialContributors;

		// federated direct message
		if (partial.containerName === '') {
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const { namedContributors } = getTitleMessageValues(intl, nonNullParticipants);
			const [name1, name2] = namedContributors;

			if (nonNullParticipants.length === 1) {
				values = { name1 };
				titleMessage = i18n.directMessageSingleUser;
			} else {
				values = { name1, name2 };
				titleMessage = i18n.directMessageTwoUsers;
			}
		}
		// federated group direct message
		else if (partial.containerName && /^mpdm-.+--.*-\d+$/.test(partial.containerName)) {
			// Extract number of people from containerName (format: mpdm-name1--name2--name3-1)
			const totalPeopleCount = (partial.containerName.match(/--/g) || []).length + 1;
			const nonNullParticipants =
				groupChatParticipants?.filter((participant) => participant?.name) || [];
			const otherCount = totalPeopleCount - nonNullParticipants.length;

			if (nonNullParticipants.length > 0) {
				const { namedContributors } = getTitleMessageValues(intl, nonNullParticipants);
				const displayedNames = namedContributors.slice(0, USER_DISPLAY_LIMIT);
				const [name1, name2, name3] = displayedNames;

				// Calculate total others (remaining known names + unknown people)
				const totalOthersCount = nonNullParticipants.length - displayedNames.length + otherCount;
				const hasOthers = totalOthersCount > 0;

				values = {
					knownNamesCount: nonNullParticipants.length,
					name1,
					name2,
					name3,
					totalOthersCount,
				};

				titleMessage = hasOthers
					? totalOthersCount === 1
						? i18n.federatedGroupDirectMessageWithOneOther
						: i18n.federatedGroupDirectMessageWithMultipleOthers
					: i18n.federatedGroupDirectMessage;
			} else {
				// Fallback if no names available
				values = { totalPeopleCount };
				titleMessage = i18n.groupDirectMessageUnknownNames;
			}
		}
		// fallback to regular thread title logic
		else {
			const result = getRegularThreadTitleValues(partial, intl, formatMessage);
			values = result.values;
			titleMessage = result.titleMessage;
		}
	} else if (
		partial?.entity?.container?.type === 'DIRECT_MESSAGE' ||
		partial?.entity?.container?.type === 'GROUP_DIRECT_MESSAGE'
	) {
		const groupChatParticipants = partial?.entity.container?.members?.map(
			(member) => member?.thirdPartyUser,
		);

		const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
			intl,
			groupChatParticipants,
		);

		const [name1, name2, name3] = namedContributors;

		values = {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
		};
		titleMessage = i18n.groupChatTitle;
	} else {
		const result = getRegularThreadTitleValues(partial, intl, formatMessage);
		values = result.values;
		titleMessage = result.titleMessage;
	}

	return <FormattedMessage {...titleMessage} values={values} />;
};
